import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Leaf } from 'lucide-react';
import { clsx } from 'clsx';
import { readWorkbookJson, writeWorkbookJson, type WorkbookActivityCompletion } from './workbookPersist';

export interface ShortWriteCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  sourcePage?: number;
  prompt: string;
  riddles?: string[];
  gridWordHints?: string[];
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type PersistShape = {
  open: string;
  riddleAnswers: Record<number, string>;
  rIndex: number;
};

export const ShortWriteCard: React.FC<ShortWriteCardProps> = ({
  workbookLabel,
  title,
  instructions,
  sourcePage,
  prompt,
  riddles = [],
  gridWordHints = [],
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-sw-${persistenceId}` : '';
  const usePerRiddle = riddles.length > 0;

  const riddlesRef = useRef(riddles);
  riddlesRef.current = riddles;
  const riddlesStableKey = riddles.join('\u0001');

  const initialMap = useMemo(() => {
    const m: Record<number, string> = {};
    riddles.forEach((_, i) => {
      m[i] = '';
    });
    return m;
  }, [riddles]);

  const [openAnswer, setOpenAnswer] = useState('');
  const [riddleAnswers, setRiddleAnswers] = useState<Record<number, string>>(initialMap);
  const [rIndex, setRIndex] = useState(0);

  useEffect(() => {
    const list = riddlesRef.current;
    const empty: Record<number, string> = {};
    list.forEach((_, i) => {
      empty[i] = '';
    });

    if (!storageKey) {
      setOpenAnswer('');
      setRiddleAnswers(empty);
      setRIndex(0);
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> | null>(storageKey, null);
    if (typeof loaded?.open === 'string') setOpenAnswer(loaded.open);
    else setOpenAnswer('');
    if (loaded?.riddleAnswers && typeof loaded.riddleAnswers === 'object') {
      const merged = { ...empty };
      const rr = loaded.riddleAnswers as Record<string, unknown>;
      for (const k of Object.keys(rr)) {
        const i = Number(k);
        if (!Number.isFinite(i)) continue;
        merged[i] = String(rr[k] ?? '');
      }
      setRiddleAnswers(merged);
    } else setRiddleAnswers(empty);
    const max = Math.max(0, list.length - 1);
    setRIndex(typeof loaded?.rIndex === 'number' ? Math.min(Math.max(0, loaded.rIndex), max) : 0);
  }, [storageKey, riddlesStableKey, riddles.length]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { open: openAnswer, riddleAnswers, rIndex } satisfies PersistShape);
  }, [storageKey, openAnswer, riddleAnswers, rIndex]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  useEffect(() => {
    if (!onWorkbookStatusChange) return;
    if (usePerRiddle && riddles.length > 0) {
      const all = riddles.every((_, i) => (riddleAnswers[i] ?? '').trim().length > 0);
      const any = riddles.some((_, i) => (riddleAnswers[i] ?? '').trim().length > 0);
      onWorkbookStatusChange(all ? 'completed' : any ? 'in_progress' : 'not_started');
    } else {
      const t = openAnswer.trim().length > 0;
      onWorkbookStatusChange(t ? 'completed' : 'not_started');
    }
  }, [onWorkbookStatusChange, usePerRiddle, riddles, riddleAnswers, openAnswer]);

  const reset = () => {
    mark();
    setOpenAnswer('');
    setRiddleAnswers({ ...initialMap });
    setRIndex(0);
  };

  const ri = Math.min(rIndex, Math.max(0, riddles.length - 1));
  const currentRiddle = riddles[ri];

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-indigo-300/90 bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 shadow-[0_20px_50px_-12px_rgba(99,102,241,0.35)] dark:border-indigo-800/60 dark:from-indigo-950/40 dark:via-slate-900 dark:to-slate-900">
      <div className="border-b border-indigo-200/70 bg-white/55 px-5 py-4 backdrop-blur-sm dark:border-indigo-800/40 dark:bg-slate-900/50">
        {workbookLabel ? (
          <p className="text-sm font-extrabold text-indigo-800 tamil-text dark:text-indigo-200" lang="ta">
            {workbookLabel}
          </p>
        ) : null}
        <h2 className="mt-1 flex items-center gap-2 text-2xl font-extrabold text-indigo-950 tamil-text md:text-3xl dark:text-indigo-50" lang="ta">
          <Leaf className="h-8 w-8 shrink-0 text-emerald-500" aria-hidden />
          {title}
        </h2>
        {instructions ? (
          <p className="mt-2 text-lg leading-relaxed text-indigo-900/95 tamil-text dark:text-indigo-100/90" lang="ta">
            {instructions}
          </p>
        ) : null}
        {sourcePage != null ? (
          <span className="mt-3 inline-block rounded-full bg-indigo-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
        ) : null}
      </div>

      <div className="px-4 py-8 sm:px-8">
        <div className="rounded-3xl border-4 border-white bg-gradient-to-r from-amber-100 to-orange-100 p-6 text-center text-xl font-extrabold leading-relaxed text-amber-950 shadow-md tamil-text dark:border-amber-900/40 dark:from-amber-950/40 dark:to-orange-950/30 dark:text-amber-50" lang="ta">
          {prompt}
        </div>

        {gridWordHints.length > 0 ? (
          <div className="mt-6">
            <p className="mb-3 text-center text-base font-extrabold text-indigo-800 tamil-text dark:text-indigo-200" lang="ta">
              உதவிக் குறிப்புச் சொற்கள்
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {gridWordHints.map((w) => (
                <span
                  key={w}
                  className="rounded-2xl border-4 border-white bg-gradient-to-r from-lime-400 to-emerald-400 px-4 py-2 text-lg font-black text-white shadow-md tamil-text"
                  lang="ta"
                >
                  {w}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {usePerRiddle && currentRiddle ? (
          <div className="mt-8">
            <div className="mb-4 flex justify-center gap-2">
              {riddles.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    mark();
                    setRIndex(i);
                  }}
                  className={clsx(
                    'h-3 w-3 rounded-full border-2 sm:h-4 sm:w-4',
                    i === ri ? 'scale-125 border-indigo-600 bg-indigo-500' : 'border-indigo-300 bg-white dark:border-indigo-700 dark:bg-slate-700'
                  )}
                  aria-label={`நொடி ${i + 1}`}
                />
              ))}
            </div>
            <div className="rounded-3xl border-4 border-white bg-white/95 p-6 shadow-inner dark:border-slate-600 dark:bg-slate-800/95">
              <p className="text-center text-sm font-extrabold text-indigo-700 tamil-text dark:text-indigo-300" lang="ta">
                நொடி {ri + 1} / {riddles.length}
              </p>
              <p className="mt-6 text-center text-2xl font-black leading-relaxed text-indigo-950 tamil-text dark:text-indigo-50" lang="ta">
                {currentRiddle}
              </p>
              <textarea
                value={riddleAnswers[ri] ?? ''}
                onChange={(e) => {
                  mark();
                  setRiddleAnswers((p) => ({ ...p, [ri]: e.target.value }));
                }}
                rows={6}
                className="mt-6 w-full min-h-[10rem] rounded-2xl border-4 border-indigo-200 bg-indigo-50/50 p-4 text-xl font-semibold leading-relaxed text-indigo-950 outline-none focus:border-amber-400 dark:border-indigo-800 dark:bg-slate-900 dark:text-white tamil-text"
                lang="ta"
                placeholder="உங்கள் விடை…"
              />
            </div>
            <div className="mt-6 flex justify-between gap-3">
              <button
                type="button"
                disabled={ri <= 0}
                onClick={() => {
                  mark();
                  setRIndex((i) => i - 1);
                }}
                className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-2xl border-4 border-indigo-300 bg-white py-3 text-base font-extrabold text-indigo-900 disabled:opacity-40 dark:border-indigo-700 dark:bg-slate-800 dark:text-indigo-100 tamil-text"
                lang="ta"
              >
                <ChevronLeft className="h-6 w-6" />
                முந்தைய
              </button>
              <button
                type="button"
                disabled={ri >= riddles.length - 1}
                onClick={() => {
                  mark();
                  setRIndex((i) => i + 1);
                }}
                className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-2xl border-4 border-indigo-300 bg-white py-3 text-base font-extrabold text-indigo-900 disabled:opacity-40 dark:border-indigo-700 dark:bg-slate-800 dark:text-indigo-100 tamil-text"
                lang="ta"
              >
                அடுத்து
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-8 rounded-3xl border-4 border-white bg-white/95 p-6 shadow-inner dark:border-slate-600 dark:bg-slate-800/95">
            <label className="block text-center text-lg font-extrabold text-indigo-900 tamil-text dark:text-indigo-100" lang="ta">
              உங்கள் எழுத்து
            </label>
            <textarea
              value={openAnswer}
              onChange={(e) => {
                mark();
                setOpenAnswer(e.target.value);
              }}
              rows={10}
              className="mt-4 w-full min-h-[14rem] rounded-2xl border-4 border-indigo-200 bg-indigo-50/40 p-4 text-xl font-semibold leading-relaxed text-indigo-950 outline-none focus:border-amber-400 dark:border-indigo-800 dark:bg-slate-900 dark:text-white tamil-text"
              lang="ta"
              placeholder="இங்கே எழுதத் தொடங்குக…"
            />
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={reset}
            className="min-h-[52px] rounded-2xl border-4 border-amber-400 bg-amber-100 px-8 py-3 text-lg font-extrabold text-amber-950 tamil-text dark:bg-amber-950/40 dark:text-amber-50"
            lang="ta"
          >
            மீண்டும் முயல்
          </button>
        </div>
      </div>
    </div>
  );
};
